/**
 * The upgrade wizard's pure model: what is selected, what it costs, and what
 * kind of billing change it is. No React, no network. Everything the screens
 * show and everything checkout sends is derived here from the v4 catalog, so
 * prices, credits and add-on sizes always come from the API.
 */
import {
  pricingMoney,
  quotePricingV4,
} from '@/containers/settings/billing/pricing-v4';
import { TIER_LABELS } from '@/constants/features';
import type {
  AppSumoPlanPrices,
  AppSumoPreviewParams,
} from '@/lib/appsumo-upgrade';
import {
  MAX_SEATS,
  MAX_SENDERS,
  UPGRADE_STEPS,
  type UpgradeInterval,
  type UpgradeSearch,
  type UpgradeStep,
  type UpgradeTier,
} from '@/lib/upgrade-link';
import type { AppSumoUpgradeOffer } from '@/store/features/appsumo.feature';
import type { PreviewPlanChangeResponse } from '@/store/features/billing.feature';
import type {
  AvailablePlans,
  CreditOption,
  Plan,
  SenderAddon,
  TrialState,
} from '@/types';

export interface UpgradeSelection {
  tier: UpgradeTier;
  interval: UpgradeInterval;
  /** Paid seats: at least the workspace's member count. */
  seats: number;
  /** Pro's extra monthly credits for the whole workspace; 0 is none, and
   *  always 0 on Starter. */
  addonCredits: number;
  /** Pro's extra senders, each 1 mailbox + 1 LinkedIn account on top of
   *  every seat's own; 0 is none, and always 0 on Starter. */
  senders: number;
}

/** The add-ons Pro had, kept while the selection visits Starter. */
export type ProAddons = Pick<UpgradeSelection, 'addonCredits' | 'senders'>;

/**
 * How the selection reaches billing:
 * - `new`: no paid subscription yet (Free, a trial without a card) → Stripe
 *   Checkout.
 * - `card_trial`: a card-backed trial → start it today (preview, then charge)
 *   or switch the plan it renews to.
 * - `change`: an existing paid subscription → preview, then change in place
 *   (or schedule a downgrade for renewal).
 * - `same`: identical to the current subscription; nothing to pay for.
 */
export type ChangeKind = 'new' | 'card_trial' | 'change' | 'same';

export type PreviewDirection = PreviewPlanChangeResponse['direction'];

/** "Starter", "Pro": the app's tier labels. */
export const tierName = (tier: UpgradeTier): string => TIER_LABELS[tier];

/**
 * The tax line under a total: Stripe Checkout works out a new subscription's
 * tax on its own page, while a change to a subscription is quoted before tax.
 */
export const taxNote = (kind: ChangeKind): string =>
  kind === 'new' ? 'Sales tax calculated at checkout' : 'Before tax';

const otherInterval = (interval: UpgradeInterval): UpgradeInterval =>
  interval === 'year' ? 'month' : 'year';

export function planForTier(
  catalog: AvailablePlans,
  tier: string
): Plan | undefined {
  return catalog.plans.find((plan) => plan.tier_key === tier);
}

export function priceFor(plan: Plan, interval: UpgradeInterval) {
  return interval === 'year' ? plan.yearly_price : plan.monthly_price;
}

/** One seat as a monthly figure: $67 for $804 a year. */
export function monthlySeatPrice(
  plan: Plan,
  interval: UpgradeInterval
): number | null {
  const price = priceFor(plan, interval)?.price;
  if (price == null) return null;
  return interval === 'year' ? price / 12 : price;
}

/** An add-on size's price for the interval, in dollars. */
export function addonPrice(option: CreditOption, interval: UpgradeInterval) {
  const cents =
    interval === 'year'
      ? option.yearly_price_cents
      : option.monthly_price_cents;
  return cents / 100;
}

/** Extra senders' price for the interval, in dollars: $5 a month or $60 a
 *  year each. Counted in cents, so any price stays exact. */
export function senderPrice(
  addon: SenderAddon,
  interval: UpgradeInterval,
  count = 1
) {
  const cents =
    interval === 'year' ? addon.yearly_price_cents : addon.monthly_price_cents;
  return (count * cents) / 100;
}

/** Pro's extra senders as the catalog sells them; null where it doesn't
 *  (billing hasn't launched them, or their prices aren't set up). */
export function senderAddonFor(catalog: AvailablePlans): SenderAddon | null {
  return planForTier(catalog, 'pro')?.sender_addon ?? null;
}

/** A subscription someone pays for. The legacy Free plan ("Free Tier") has no
 *  tier key, so its name is checked as well. */
export function isPaidPlan(plan: Plan | null | undefined): plan is Plan {
  return !!plan && plan.tier_key !== 'free' && !/^free\b/i.test(plan.name);
}

/** A card-backed trial: it has a subscription that renews to a paid plan. */
export function isCardTrial(trial: TrialState | null): boolean {
  return trial?.active === true && !!trial.renews_to;
}

/** Paid and on this (v4) catalog, so its seats and add-on carry over. */
/**
 * True for a workspace on a contract: Enterprise, Scale or a custom catalog.
 * Sales owns its plan, and a self-serve order would replace the contract's
 * subscription. Legacy Enterprise tiers ("Enterprise Tier I–III") carry no
 * tier key, so their name is checked as well.
 */
export function isContractPlan(catalog: AvailablePlans): boolean {
  const current = catalog.current_plan;
  const tier = current?.tier_key;
  return (
    tier === 'enterprise' ||
    tier === 'scale' ||
    (!tier && /\benterprise\b/i.test(current?.name ?? '')) ||
    catalog.catalog_family === 'custom'
  );
}

export function isCurrentV4Paid(catalog: AvailablePlans): boolean {
  const current = catalog.current_plan;
  return (
    isPaidPlan(current) &&
    (current.pricing_version === 'v4' ||
      catalog.plans.some((plan) => plan.id === current.id))
  );
}

/** Seats can't go below the people already in the workspace. */
export function minSeats(catalog: AvailablePlans): number {
  return Math.max(1, Math.ceil(catalog.seats_used || 1));
}

function clampSeats(seats: number, min: number): number {
  const whole = Number.isFinite(seats) ? Math.round(seats) : min;
  return Math.max(min, Math.min(MAX_SEATS, whole));
}

function clampSenders(senders: number): number {
  const whole = Number.isFinite(senders) ? Math.round(senders) : 0;
  return Math.max(0, Math.min(MAX_SENDERS, whole));
}

/** The workspace's current Pro credit add-on size, as billing sends it; 0
 *  when it has none. */
export function currentAddonCredits(catalog: AvailablePlans): number {
  const current = catalog.current_plan;
  return current?.tier_key === 'pro' ? (current.credit_addon_credits ?? 0) : 0;
}

/**
 * The add-on a card trial renews into: billing folds it into
 * `renews_to.credits`, so it is what the renewal carries beyond its seats'
 * own credits. 0 when the trial renews to no Pro add-on, or to a size the
 * catalog doesn't sell.
 */
export function renewalAddonCredits(
  catalog: AvailablePlans,
  trial: TrialState | null
): number {
  const renewal = isCardTrial(trial) ? trial?.renews_to : null;
  if (!renewal || renewal.plan !== 'pro') return 0;
  const pro = planForTier(catalog, 'pro');
  const extra = renewal.credits - (pro?.credits_per_seat ?? 0) * renewal.seats;
  return addonOption(pro, extra) ? extra : 0;
}

/**
 * The Pro add-on the workspace has: its subscription's, or on a card trial
 * (where the plan reads Free) the one the trial renews into.
 */
export function workspaceAddonCredits(
  catalog: AvailablePlans,
  trial: TrialState | null
): number {
  return currentAddonCredits(catalog) || renewalAddonCredits(catalog, trial);
}

/** The workspace's current extra senders, as billing sends them; 0 when it
 *  has none. */
export function currentSenders(catalog: AvailablePlans): number {
  const current = catalog.current_plan;
  return current?.tier_key === 'pro' ? (current.sender_addon_quantity ?? 0) : 0;
}

/**
 * The seats a card trial renews into, when the renewal is one of the
 * catalog's plans: its credits are its seats' per-seat credits plus nothing
 * or an add-on the catalog sells. Null otherwise. A trial on earlier pricing
 * reports its plan's included seats (5 on Pro), which aren't seats anyone
 * chose.
 */
export function renewalSeats(
  catalog: AvailablePlans,
  trial: TrialState | null
): number | null {
  const renewal = isCardTrial(trial) ? trial?.renews_to : null;
  const plan = renewal ? planForTier(catalog, renewal.plan) : undefined;
  if (!renewal || !plan?.credits_per_seat) return null;
  const extra = renewal.credits - plan.credits_per_seat * renewal.seats;
  return extra === 0 || addonOption(plan, extra) ? renewal.seats : null;
}

/** The extra senders a card trial renews into; 0 when it renews to none,
 *  or to Starter, which has none. */
export function renewalSenders(trial: TrialState | null): number {
  const renewal = isCardTrial(trial) ? trial?.renews_to : null;
  return renewal?.plan === 'pro' ? (renewal.sender_addon_quantity ?? 0) : 0;
}

/**
 * The extra senders the workspace has: its subscription's, or on a card
 * trial (where the plan reads Free) the ones the trial renews into.
 */
export function workspaceSenders(
  catalog: AvailablePlans,
  trial: TrialState | null
): number {
  return currentSenders(catalog) || renewalSenders(trial);
}

/**
 * True when a Pro order would drop extra senders the workspace pays for (or
 * a card trial renews into) only because the catalog can't price them right
 * now: billing sells none while its launch flag reads off. Checkout sends
 * the whole subscription, so such an order isn't sent.
 */
export function dropsUnpricedSenders(
  catalog: AvailablePlans,
  trial: TrialState | null,
  selection: UpgradeSelection
): boolean {
  return (
    selection.tier === 'pro' &&
    workspaceSenders(catalog, trial) > 0 &&
    !senderAddonFor(catalog)
  );
}

/**
 * What the workspace's extra senders cost on its billing interval, in
 * dollars: their count at the catalog's price. 0 without any; null when the
 * subscription pays a price the catalog doesn't sell, so their share of the
 * total can't be read back.
 */
function currentSendersPrice(catalog: AvailablePlans): number | null {
  const count = currentSenders(catalog);
  if (count === 0) return 0;
  const interval = catalog.current_billing_interval;
  const addon = senderAddonFor(catalog);
  if (!interval || !addon) return null;
  const paid = catalog.current_plan?.sender_addon_price_id;
  const todays =
    interval === 'year' ? addon.yearly_price_id : addon.monthly_price_id;
  if (paid && paid !== todays) return null;
  return senderPrice(addon, interval, count);
}

/**
 * The seats the workspace's v4 subscription bills for, read back from the
 * plan's monthly credits (seats × credits per seat, plus the add-on; extra
 * senders bring none); null when that doesn't come out whole, or without a
 * paid v4 plan.
 */
function seatsFromCredits(catalog: AvailablePlans): number | null {
  const current = catalog.current_plan;
  if (!current || !isCurrentV4Paid(catalog)) return null;
  const tierPlan = planForTier(catalog, current.tier_key ?? '');
  const perSeat = tierPlan?.credits_per_seat;
  const seatCredits =
    (current.monthly_credits ?? 0) - currentAddonCredits(catalog);
  const seats = perSeat ? seatCredits / perSeat : 0;
  return Number.isInteger(seats) && seats > 0 ? seats : null;
}

/**
 * The seats the workspace's v4 subscription bills for, or null without one
 * (Free, a trial, or a plan on earlier pricing). `seats_paid` also counts
 * seats an AppSumo license covers, so the count is read back from the plan's
 * credits when that comes out whole, and taken from `seats_paid` when it
 * doesn't.
 */
export function billedSeats(catalog: AvailablePlans): number | null {
  if (!isCurrentV4Paid(catalog)) return null;
  return seatsFromCredits(catalog) ?? catalog.seats_paid;
}

/**
 * What the workspace's extra credits cost on its billing interval, in
 * dollars. Billing prices the subscription as its seats at the plan's price,
 * the add-on at what the subscription pays for it and any extra senders, so
 * the add-on is the total less the seats and senders. Null unless the seats
 * (read back from the credits, not `seats_paid`) and the senders' price are
 * known exactly, as the remainder is only the add-on's then.
 */
function currentAddonPrice(catalog: AvailablePlans): number | null {
  const current = catalog.current_plan;
  const interval = catalog.current_billing_interval;
  const seats = seatsFromCredits(catalog);
  const senders = currentSendersPrice(catalog);
  const tierPlan = planForTier(catalog, current?.tier_key ?? '');
  if (!current || !interval || seats == null || senders == null || !tierPlan) {
    return null;
  }
  if (currentAddonCredits(catalog) <= 0) return null;
  const total = priceFor(current, interval)?.price;
  const seatPrice = priceFor(tierPlan, interval)?.price;
  if (total == null || seatPrice == null) return null;
  // Whole cents: the prices are dollars as floats.
  const addon = Math.round((total - seats * seatPrice - senders) * 100) / 100;
  return addon > 0 ? addon : null;
}

/**
 * The workspace's extra credits when they bill at an earlier price than
 * today's for their size (an add-on bought before today's pricing).
 * Checkout replaces every item on the subscription, so any change moves
 * them to today's price. `amount` is what they cost now on the workspace's
 * interval, in dollars, or null when that can't be read back exactly. Null
 * when they are on today's price or cost the same on the earlier one, and
 * without extra credits.
 */
export function earlierAddonPrice(
  catalog: AvailablePlans
): { amount: number | null } | null {
  const priceId = catalog.current_plan?.credit_addon_price_id;
  const interval = catalog.current_billing_interval;
  const option = addonOption(
    planForTier(catalog, 'pro'),
    currentAddonCredits(catalog)
  );
  if (!priceId || !interval || !option) return null;
  const todays =
    interval === 'year' ? option.yearly_price_id : option.monthly_price_id;
  if (priceId === todays) return null;
  const amount = currentAddonPrice(catalog);
  if (amount === addonPrice(option, interval)) return null;
  return { amount };
}

export function addonOption(
  plan: Plan | undefined,
  credits: number
): CreditOption | undefined {
  if (!plan || credits <= 0) return undefined;
  return plan.credit_options?.find((option) => option.credits === credits);
}

function resolveTier(
  catalog: AvailablePlans,
  requested: UpgradeTier | undefined
): UpgradeTier {
  const order: UpgradeTier[] =
    requested === 'starter' ? ['starter', 'pro'] : ['pro', 'starter'];
  return order.find((tier) => planForTier(catalog, tier)) ?? 'pro';
}

/**
 * The selection the wizard opens with: the URL's values where it has them,
 * the workspace's current subscription where it doesn't, then Pro, annual and
 * one seat per member. Invalid values are clamped or dropped, never trusted.
 */
export function resolveSelection(
  catalog: AvailablePlans,
  trial: TrialState | null,
  search: UpgradeSearch
): UpgradeSelection {
  const tier = resolveTier(catalog, search.plan);
  const plan = planForTier(catalog, tier);
  const renewal = isCardTrial(trial) ? trial?.renews_to : null;
  const preferred: UpgradeInterval =
    search.interval ??
    (isPaidPlan(catalog.current_plan)
      ? catalog.current_billing_interval
      : renewal?.interval) ??
    'year';
  // A plan priced on one interval only gets that interval.
  const interval =
    plan &&
    !priceFor(plan, preferred) &&
    priceFor(plan, otherInterval(preferred))
      ? otherInterval(preferred)
      : preferred;
  const defaultSeats =
    billedSeats(catalog) ?? renewalSeats(catalog, trial) ?? catalog.seats_used;
  const seats = clampSeats(search.seats ?? defaultSeats, minSeats(catalog));
  return {
    tier,
    interval,
    seats,
    addonCredits: resolveAddon(catalog, trial, tier, search.credits),
    senders: resolveSenders(catalog, trial, tier, search.senders),
  };
}

function resolveAddon(
  catalog: AvailablePlans,
  trial: TrialState | null,
  tier: UpgradeTier,
  requested: number | undefined
): number {
  if (tier !== 'pro') return 0;
  const wanted = requested ?? workspaceAddonCredits(catalog, trial);
  return addonOption(planForTier(catalog, 'pro'), wanted) ? wanted : 0;
}

function resolveSenders(
  catalog: AvailablePlans,
  trial: TrialState | null,
  tier: UpgradeTier,
  requested: number | undefined
): number {
  if (tier !== 'pro' || !senderAddonFor(catalog)) return 0;
  return clampSenders(requested ?? workspaceSenders(catalog, trial));
}

/**
 * Applies a change to the selection, keeping it valid: seats stay within
 * bounds and only Pro has add-ons. Starter drops them, so a move back to Pro
 * takes `proAddons` (what Pro had) unless the change names them.
 */
export function updateSelection(
  catalog: AvailablePlans,
  selection: UpgradeSelection,
  patch: Partial<UpgradeSelection>,
  proAddons: ProAddons
): UpgradeSelection {
  const next = { ...selection, ...patch };
  const fromStarter = selection.tier === 'starter';
  const credits =
    fromStarter && patch.addonCredits === undefined
      ? proAddons.addonCredits
      : next.addonCredits;
  const senders =
    fromStarter && patch.senders === undefined
      ? proAddons.senders
      : next.senders;
  const onPro = next.tier === 'pro';
  return {
    ...next,
    seats: clampSeats(next.seats, minSeats(catalog)),
    addonCredits:
      onPro && addonOption(planForTier(catalog, 'pro'), credits) ? credits : 0,
    senders: onPro && senderAddonFor(catalog) ? clampSenders(senders) : 0,
  };
}

/** The steps a tier walks through. Starter has no add-ons, so it skips them
 *  (the stepper still shows that segment, muted), and so does Pro where the
 *  catalog sells it none (the AppSumo member price). */
export function stepsFor(tier: UpgradeTier, addons = true): UpgradeStep[] {
  return tier === 'pro' && addons
    ? ['plan', 'addons', 'review']
    : ['plan', 'review'];
}

export function nextStep(
  step: UpgradeStep,
  tier: UpgradeTier,
  addons = true
): UpgradeStep | null {
  const at = UPGRADE_STEPS.indexOf(step);
  return (
    stepsFor(tier, addons).find((s) => UPGRADE_STEPS.indexOf(s) > at) ?? null
  );
}

export function previousStep(
  step: UpgradeStep,
  tier: UpgradeTier,
  addons = true
): UpgradeStep | null {
  const at = UPGRADE_STEPS.indexOf(step);
  return (
    [...stepsFor(tier, addons)]
      .reverse()
      .find((s) => UPGRADE_STEPS.indexOf(s) < at) ?? null
  );
}

/** "Next: Add-ons" or "Next: Review". */
export function nextStepLabel(
  step: UpgradeStep,
  tier: UpgradeTier,
  addons = true
): string {
  return nextStep(step, tier, addons) === 'addons'
    ? 'Next: Add-ons'
    : 'Next: Review';
}

/**
 * An AppSumo-only workspace's member price while the server's offer is open:
 * Starter and Pro a seat a month, each beside its regular price, until the
 * offer's end. Monthly only, with no credits of their own (the deal's keep
 * coming) and no add-ons. Null for everyone else, and once the offer closes.
 */
export interface MemberPricing {
  endsAt: string;
  /** Cents a seat a month: the member price and the regular price. */
  plans: Record<UpgradeTier, { offerCents: number; listCents: number }>;
  /** The deal's credits a month; null when the offer doesn't say. */
  dealCredits: number | null;
  /** The workspace's members, the fewest seats the AppSumo checkout takes. */
  members: number;
}

/** The member price from `useAppSumoMemberOffer()`, whose `active` already means
 *  an AppSumo-only workspace, eligible, both plans priced and the offer
 *  open. */
export function memberPricing({
  active,
  offer,
  prices,
}: {
  active: boolean;
  offer: Pick<AppSumoUpgradeOffer, 'ends_at' | 'keeps' | 'members'> | undefined;
  prices: AppSumoPlanPrices | null;
}): MemberPricing | null {
  if (!active || !offer || !prices) return null;
  const plan = (key: UpgradeTier) => ({
    offerCents: prices[key].offer_cents,
    listCents: prices[key].list_cents,
  });
  const credits = offer.keeps?.monthly_credits;
  return {
    endsAt: offer.ends_at,
    plans: { starter: plan('starter'), pro: plan('pro') },
    dealCredits:
      typeof credits === 'number' && Number.isFinite(credits) && credits >= 0
        ? credits
        : null,
    members: Math.max(1, Math.ceil(offer.members || 1)),
  };
}

/** Catalog features about credit levels and top-ups, which the member
 *  price doesn't sell (its credits are the deal's). */
const CREDIT_SALE_FEATURE = /\bcredit (levels?|top-ups?)\b/i;

/**
 * The catalog the wizard sells from: as it is, or with Starter and Pro at
 * the member price. Those are monthly only and bring no credits or add-ons,
 * so the selection, the quote and the summary need nothing of their own.
 * The price ids only name the plan: the AppSumo checkout takes the plan and
 * seats, and the server prices it. Seats start at the larger of the
 * catalog's and the offer's member counts, as the checkout refuses fewer
 * than the offer's.
 */
export function withMemberPrices(
  catalog: AvailablePlans,
  member: MemberPricing | null
): AvailablePlans {
  if (!member) return catalog;
  return {
    ...catalog,
    seats_used: Math.max(catalog.seats_used || 0, member.members),
    plans: catalog.plans.map((plan) => {
      const tier = plan.tier_key;
      if (tier !== 'starter' && tier !== 'pro') return plan;
      return {
        ...plan,
        monthly_price: {
          id: `appsumo:${tier}`,
          price: member.plans[tier].offerCents / 100,
          interval: 'month',
        },
        yearly_price: null,
        credits_per_seat: 0,
        monthly_credits: 0,
        credit_options: [],
        sender_addon: null,
        features: (plan.features ?? []).filter(
          (feature) => !CREDIT_SALE_FEATURE.test(feature)
        ),
      };
    }),
  };
}

/** What a seat saves a month at the member price, in cents. */
export function memberSavingCents(
  member: MemberPricing,
  tier: UpgradeTier
): number {
  const { offerCents, listCents } = member.plans[tier];
  return Math.max(0, listCents - offerCents);
}

/** The seats at the regular price for a month, in dollars. */
export function memberRegularTotal(
  member: MemberPricing,
  tier: UpgradeTier,
  seats: number
): number {
  return (member.plans[tier].listCents * seats) / 100;
}

/** What a year of monthly billing costs over annual billing, for one seat. */
export function annualSavingsPerSeat(plan: Plan): number | null {
  const monthly = plan.monthly_price?.price;
  const yearly = plan.yearly_price?.price;
  if (monthly == null || yearly == null) return null;
  const saving = monthly * 12 - yearly;
  return saving > 0 ? saving : null;
}

/** Annual billing's discount on a seat, as a whole percent (Pro: 25). */
export function annualSavingsPercent(plan: Plan): number | null {
  const monthly = plan.monthly_price?.price;
  const saving = annualSavingsPerSeat(plan);
  if (!monthly || saving == null) return null;
  const percent = Math.round((saving / (monthly * 12)) * 100);
  return percent > 0 ? percent : null;
}

/**
 * The annual discount across the self-serve plans, for the billing toggle:
 * the best one, and whether the plans differ (so the chip reads "Save up to
 * 25%" and doesn't change as the user moves between plans).
 */
export function catalogAnnualSavings(
  catalog: AvailablePlans
): { percent: number; upTo: boolean } | null {
  const percents = (['starter', 'pro'] as const)
    .map((tier) => planForTier(catalog, tier))
    .map((plan) => (plan ? annualSavingsPercent(plan) : null))
    .filter((percent): percent is number => percent != null);
  if (percents.length === 0) return null;
  const best = Math.max(...percents);
  return { percent: best, upTo: percents.some((p) => p !== best) };
}

export interface UpgradeQuote {
  seats: number;
  /** One seat for the interval: $89 a month, or $804 a year. */
  perSeat: number | null;
  /** Every seat for the interval. */
  seatsTotal: number | null;
  /** The credit add-on for the interval; 0 without one. */
  addonTotal: number;
  /** Extra senders; 0 without any. */
  senders: number;
  /** One extra sender for the interval: $5 a month, or $60 a year; null
   *  where the plan doesn't sell them. */
  perSender: number | null;
  /** Every extra sender for the interval; 0 without any. */
  sendersTotal: number;
  /** Seats, add-on and senders for the interval. */
  total: number | null;
  /** The total as a monthly figure. */
  monthlyEquivalent: number | null;
  /** Seats × credits per seat. */
  planCredits: number;
  addonCredits: number;
  /** The workspace's credits a month, plan plus add-on. */
  credits: number;
  priceId?: string;
  addonPriceId?: string;
  senderPriceId?: string;
  /** Checkout can be offered: the plan (and its add-ons) are priced. */
  available: boolean;
}

/** Prices the selection. Seats multiply the plan; the credit add-on is billed
 *  once for the workspace and each extra sender once, all at the plan's
 *  interval. */
export function quoteSelection(
  plan: Plan,
  interval: UpgradeInterval,
  seats: number,
  addon?: CreditOption,
  senders = 0
): UpgradeQuote {
  const base = quotePricingV4(plan, interval, seats, addon);
  const perSeat = priceFor(plan, interval)?.price ?? null;
  const senderAddon = plan.sender_addon ?? null;
  const perSender = senderAddon ? senderPrice(senderAddon, interval) : null;
  const sendersTotal = senderAddon
    ? senderPrice(senderAddon, interval, senders)
    : 0;
  const senderPriceId =
    senders > 0 && senderAddon
      ? interval === 'year'
        ? senderAddon.yearly_price_id
        : senderAddon.monthly_price_id
      : undefined;
  const total = base.total == null ? null : base.total + sendersTotal;
  return {
    seats: base.seats,
    perSeat,
    seatsTotal: perSeat == null ? null : perSeat * base.seats,
    addonTotal: addon ? addonPrice(addon, interval) : 0,
    senders,
    perSender,
    sendersTotal,
    total,
    monthlyEquivalent:
      total == null ? null : interval === 'year' ? total / 12 : total,
    planCredits: base.seats * (plan.credits_per_seat ?? 0),
    addonCredits: addon?.credits ?? 0,
    credits: base.credits,
    priceId: base.priceId,
    addonPriceId: base.addonPriceId,
    senderPriceId,
    // Senders need a price for the interval, as the add-on does.
    available: base.available && (senders === 0 || !!senderPriceId),
  };
}

/** True when the selection is exactly the workspace's v4 subscription. */
function isCurrentSelection(
  catalog: AvailablePlans,
  selection: UpgradeSelection
): boolean {
  const current = catalog.current_plan;
  if (!isCurrentV4Paid(catalog) || !current) return false;
  return (
    current.tier_key === selection.tier &&
    catalog.current_billing_interval === selection.interval &&
    billedSeats(catalog) === selection.seats &&
    currentAddonCredits(catalog) === selection.addonCredits &&
    currentSenders(catalog) === selection.senders
  );
}

/**
 * True when a card trial already renews to exactly this selection, so there
 * is no renewal plan to switch to. The renewal's credits are its seats' plus
 * the credit add-on, which is how the add-on is read back; it names its
 * extra senders.
 */
export function isTrialRenewalSelection(
  catalog: AvailablePlans,
  selection: UpgradeSelection,
  trial: TrialState | null
): boolean {
  const renewal = isCardTrial(trial) ? trial?.renews_to : null;
  if (!renewal) return false;
  const perSeat = planForTier(catalog, selection.tier)?.credits_per_seat ?? 0;
  return (
    renewal.plan === selection.tier &&
    renewal.interval === selection.interval &&
    renewal.seats === selection.seats &&
    renewal.credits - perSeat * renewal.seats === selection.addonCredits &&
    (renewal.sender_addon_quantity ?? 0) === selection.senders
  );
}

export function changeKind(
  catalog: AvailablePlans,
  selection: UpgradeSelection,
  trial: TrialState | null
): ChangeKind {
  if (isCardTrial(trial)) return 'card_trial';
  if (!isPaidPlan(catalog.current_plan)) return 'new';
  return isCurrentSelection(catalog, selection) ? 'same' : 'change';
}

export interface PayCtaInput {
  kind: ChangeKind;
  tierLabel: string;
  /** Today's charge from the billing preview, in dollars. */
  dueToday?: number | null;
  direction?: PreviewDirection | null;
}

/** The review step's pay button. */
export function payCtaLabel({
  kind,
  tierLabel,
  dueToday,
  direction,
}: PayCtaInput): string {
  switch (kind) {
    case 'new':
      return 'Continue to secure checkout';
    case 'same':
      return 'This is your current plan';
    case 'card_trial':
      return dueToday != null
        ? `Start ${tierLabel} today · ${pricingMoney(dueToday)}`
        : `Start ${tierLabel} today`;
    case 'change':
      if (direction === 'downgrade') return 'Schedule change';
      if (direction === 'upgrade') {
        return dueToday != null && dueToday >= 0.01
          ? `Pay ${pricingMoney(dueToday)} and upgrade`
          : 'Confirm upgrade';
      }
      return 'Confirm change';
  }
}

/** Why the review step's pay button is unavailable, or null when it isn't. */
export function payBlockedReason({
  kind,
  notAdmin,
  planEnding = false,
  canRenew = false,
  hasOrder,
  sendersUnpriced,
  previewFailed,
}: {
  kind: ChangeKind;
  notAdmin: boolean;
  /** The plan is cancelled and set to end: billing refuses a change until
   *  it's renewed. */
  planEnding?: boolean;
  /** Renew is offered right under the reason (an admin, toggle on). */
  canRenew?: boolean;
  /** The plan is sold at the chosen interval. */
  hasOrder: boolean;
  /** The order would drop extra senders the catalog can't price now. */
  sendersUnpriced: boolean;
  /** Billing couldn't preview the change, so its charge is unknown. */
  previewFailed: boolean;
}): string | null {
  if (notAdmin) return 'Only workspace admins can change the plan.';
  if (planEnding) {
    return canRenew
      ? 'Renew your previous plan to continue.'
      : 'Renew your previous plan from Plans & billing to continue.';
  }
  if (kind === 'same') return 'Change seats, billing or add-ons to continue.';
  if (!hasOrder) return 'This plan isn’t offered with this billing period.';
  if (sendersUnpriced) {
    return 'We can’t keep your extra senders in this change right now. Try again shortly.';
  }
  if (previewFailed) {
    return 'We couldn’t confirm the price. Nothing has been charged.';
  }
  return null;
}

export interface ReviewSublineInput {
  kind: ChangeKind;
  tierLabel: string;
  /** Billing schedules the change for renewal instead of charging today. */
  downgrade: boolean;
  /** The renewal a scheduled change takes effect on: "Nov 14, 2026". */
  effectiveOn: string | null;
  /** The card trial's last day: "Oct 27". */
  trialEndsOn: string | null;
  /** The card trial already becomes this selection. */
  renewsAsIs: boolean;
}

/** The review step's line under its title: what confirming will do. */
export function reviewSubline({
  kind,
  tierLabel,
  downgrade,
  effectiveOn,
  trialEndsOn,
  renewsAsIs,
}: ReviewSublineInput): string {
  switch (kind) {
    case 'new':
      return 'You’ll enter payment details on Stripe’s secure checkout.';
    case 'same':
      return 'This is your current plan. Change seats, billing or add-ons to continue.';
    case 'card_trial':
      if (renewsAsIs) {
        const when = trialEndsOn ? `on ${trialEndsOn}` : 'when it ends';
        return `Your trial already becomes this plan ${when}, so there’s nothing to change. Start ${tierLabel} today if you’d rather not wait.`;
      }
      return `Your trial ends ${trialEndsOn ?? 'soon'}. Start ${tierLabel} today, or keep your trial and switch the plan it becomes.`;
    case 'change':
      return downgrade
        ? `Takes effect on ${effectiveOn ?? 'your next renewal'}. Nothing is charged today.`
        : 'We’ll charge your card on file.';
  }
}

/** The URL search for a selection on a step. `preview` is the AppSumo
 *  offer's dev preview (DEV builds only), kept so it survives the update. */
export function selectionSearch(
  selection: UpgradeSelection,
  step: UpgradeStep,
  source: string | undefined,
  preview: AppSumoPreviewParams = {}
): UpgradeSearch {
  return {
    ...preview,
    step,
    plan: selection.tier,
    interval: selection.interval,
    seats: selection.seats,
    // Explicit 0s on Pro, so removed add-ons stay removed on reload.
    credits: selection.tier === 'pro' ? selection.addonCredits : undefined,
    senders: selection.tier === 'pro' ? selection.senders : undefined,
    source,
  };
}

const SEARCH_KEYS = [
  'step',
  'plan',
  'interval',
  'seats',
  'credits',
  'senders',
  'source',
] as const satisfies readonly (keyof UpgradeSearch)[];

export const sameSearch = (a: UpgradeSearch, b: UpgradeSearch) =>
  SEARCH_KEYS.every((key) => a[key] === b[key]);

/** "1 seat", "3 seats"; `many` for an irregular plural ("mailboxes"). */
export const plural = (count: number, one: string, many = `${one}s`) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;

/** A price for the interval, compact: "$140/mo" or "$1,680/yr". */
export const pricePerInterval = (amount: number, interval: UpgradeInterval) =>
  `${pricingMoney(amount)}/${interval === 'year' ? 'yr' : 'mo'}`;

/** One credits formatter for the wizard and the education below it. */
